import { COLOR_BY_NUMBER_CELLS } from "../ColorByNumberPicture.ts";
import { RIDDLE_MAX_LETTERS } from "../RiddleGenerator.ts";
import { distinctMagicSquares } from "../MagicSquareGenerator.ts";
import { distinctRomanValues } from "../RomanNumeralGenerator.ts";
import { SINGLE_ANSWER_LINE_PLOT_QUESTIONS } from "../LinePlotGenerator.ts";
import { CALENDAR_HOW_MANY_QUESTIONS } from "../CalendarGenerator.ts";
import { SINGLE_ANSWER_HISTOGRAM_QUESTIONS } from "../HistogramGenerator.ts";
import { SINGLE_ANSWER_BOX_PLOT_QUESTIONS } from "../BoxPlotGenerator.ts";
import {
    FullPageLongDivisionNumberOfProblemsOptions,
    FullPageLargePrintMoneyOptions,
    FullPageLargePrintOptions,
    FullPageNumberOfBarGraphOptions,
    FullPageNumberOfCenterAndSpreadLinePlotOptions,
    FullPageNumberOfHistogramOptions,
    FullPageNumberOfSingleAnswerHistogramOptions,
    FullPageNumberOfBoxPlotOptions,
    FullPageNumberOfSingleAnswerBoxPlotOptions,
    FullPageNumberOfQuartileBoxPlotOptions,
    FullPageNumberOfCoordinateOptions,
    FullPageNumberOfSlopeGraphOptions,
    FullPageNumberOfSlopePointsOptions,
    FullPageNumberOfCalendarOptions,
    HalfPageNumberOfCalendarOptions,
    FullPageNumberOfTriangleOptions,
    FullPageNumberOfMissingCornerOptions,
    FullPageNumberOfMagicSquareOptions,
    FullPageNumberOfLinePlotOptions,
    FullPageNumberOfSingleAnswerLinePlotOptions,
    FullPageNumberOfSingleAnswerBarGraphOptions,
    FullPageNumberOfChangeProblemsOptions,
    FullPageNumberOfCoinProblemsOptions,
    FullPageNumberOfAmPmOptions,
    FullPageNumberOfElapsedDigitalOptions,
    FullPageNumberOfElapsedNumberLineOptions,
    FullPageNumberOfElapsedTwoClockOptions,
    FullPageNumberOfFactFamilyOptions,
    FullPageNumberOfFractionModelOptions,
    FullPageNumberOfFractionProblemsOptions,
    FullPageNumberOfGeometryOptions,
    FullPageNumberOfMissingNumberOptions,
    FullPageNumberOfMoneyProblemsOptions,
    FullPageNumberOfNumberLineOptions,
    FullPageNumberOfOrderOfOperationsOptions,
    FullPageNumberOfPercentMoneyOptions,
    FullPageNumberOfPlaceValueProblemsOptions,
    FullPageNumberOfProblemsOptions,
    FullPageNumberOfProportionalGraphOptions,
    FullPageNumberOfProportionalOptions,
    FullPageNumberOfRationalOptions,
    FullPageNumberOfWritingExpressionOptions,
    FullPageNumberOfSystemsOptions,
    FullPageNumberOfRoundingComparingOptions,
    FullPageNumberOfRoundingNumberLineOptions,
    FullPageNumberOfAreaFigureOptions,
    FullPageNumberOfScaleDrawingOptions,
    FullPageNumberOfShapeOptions,
    FullPageNumberOfVolumeOptions,
    FullPageNumberOfCircleOptions,
    FullPageNumberOfProbabilityOptions,
    FullPageNumberOfPythagoreanOptions,
    FullPageNumberOfSkipCountingOptions,
    FullPageNumberOfTimeProblemsOptions,
    FullPageNumberOfWordProblemsOptions,
    HalfPageLongDivisionNumberOfProblemsOptions,
    HalfPageLargePrintMoneyOptions,
    HalfPageLargePrintOptions,
    HalfPageNumberOfBarGraphOptions,
    HalfPageNumberOfCenterAndSpreadLinePlotOptions,
    HalfPageNumberOfHistogramOptions,
    HalfPageNumberOfSingleAnswerHistogramOptions,
    HalfPageNumberOfBoxPlotOptions,
    HalfPageNumberOfSingleAnswerBoxPlotOptions,
    HalfPageNumberOfQuartileBoxPlotOptions,
    HalfPageNumberOfCoordinateOptions,
    HalfPageNumberOfSlopeGraphOptions,
    HalfPageNumberOfSlopePointsOptions,
    HalfPageNumberOfTriangleOptions,
    HalfPageNumberOfMissingCornerOptions,
    HalfPageNumberOfMagicSquareOptions,
    HalfPageNumberOfLinePlotOptions,
    HalfPageNumberOfSingleAnswerLinePlotOptions,
    HalfPageNumberOfSingleAnswerBarGraphOptions,
    HalfPageNumberOfChangeProblemsOptions,
    HalfPageNumberOfCoinProblemsOptions,
    HalfPageNumberOfAmPmOptions,
    HalfPageNumberOfElapsedDigitalOptions,
    HalfPageNumberOfElapsedNumberLineOptions,
    HalfPageNumberOfElapsedTwoClockOptions,
    HalfPageNumberOfFactFamilyOptions,
    HalfPageNumberOfFractionModelOptions,
    HalfPageNumberOfFractionProblemsOptions,
    HalfPageNumberOfGeometryOptions,
    HalfPageNumberOfMissingNumberOptions,
    HalfPageNumberOfMoneyProblemsOptions,
    HalfPageNumberOfNumberLineOptions,
    HalfPageNumberOfOrderOfOperationsOptions,
    HalfPageNumberOfPercentMoneyOptions,
    HalfPageNumberOfPlaceValueProblemsOptions,
    HalfPageNumberOfProblemsOptions,
    HalfPageNumberOfProportionalGraphOptions,
    HalfPageNumberOfProportionalOptions,
    HalfPageNumberOfRationalOptions,
    HalfPageNumberOfWritingExpressionOptions,
    HalfPageNumberOfSystemsOptions,
    HalfPageNumberOfRoundingComparingOptions,
    HalfPageNumberOfRoundingNumberLineOptions,
    HalfPageNumberOfAreaFigureOptions,
    HalfPageNumberOfScaleDrawingOptions,
    HalfPageNumberOfShapeOptions,
    HalfPageNumberOfVolumeOptions,
    HalfPageNumberOfCircleOptions,
    HalfPageNumberOfProbabilityOptions,
    HalfPageNumberOfPythagoreanOptions,
    HalfPageNumberOfTimeProblemsOptions,
    HalfPageNumberOfWordProblemsOptions,
    ISettingsProps,
} from "../Models.ts";
import {
    AlgebraQuestionOptions,
    BarGraphQuestionOptions,
    CalendarQuestionOptions,
    BoxPlotQuestionOptions,
    CoordinateQuestionOptions,
    LinePlotQuestionOptions,
    isAreaFigureQuestion,
    isElapsedTimeQuestion,
    isFractionModelQuestion,
    isRationalQuestion,
    MissingNumberOptions,
    MoneyQuestionOptions,
    Operations,
    MONEY_PERCENT_QUESTIONS,
    PatternQuestionOptions,
    ProblemFormatOptions,
    RatioQuestionOptions,
    ShapeQuestionOptions,
    RoundingDisplayOptions,
    TimeDisplayOptions,
    TimeQuestionOptions,
} from "../Constants.ts";

/**
 * Which per-page counts a sheet may be set to.
 *
 * Lifted out of `NumberOfProblems.tsx`, where it was interleaved with the
 * radio buttons that render it, so that **the page-count measurement script
 * can ask the same question the settings panel asks.** `scripts/measurePages`
 * sweeps every format at every offered count against the real renderer; a
 * second copy of this table there would drift from this one, and the drift
 * would be silent in the worst direction - the script would report a clean
 * sweep over counts the panel does not offer while missing ones it does.
 * That is the `footerGroupOf()` / `isLinkable` hazard CLAUDE.md warns about,
 * and this is the same answer: state it once, import it twice.
 *
 * The branch order is load-bearing and is preserved exactly as it was. Later
 * branches overwrite earlier ones, so the fraction-model check sits inside the
 * Fractions branch rather than after it, and long division overrides the base
 * math-facts counts before the missing-number branch can look at them.
 *
 * Every array here is measured against the rendered PDF rather than reasoned
 * about; see the arrays' own comments in `Models.ts`, and ADRs 0047, 0048 and
 * 0049 for the three times reasoning about them was wrong.
 */
export function perPageOptionsFor(settings: ISettingsProps): number[] {
    let options = settings.HalfPage
        ? HalfPageNumberOfProblemsOptions
        : FullPageNumberOfProblemsOptions;

    const isLongDivision =
        (settings.Operators?.findIndex((x) => x.value === Operations.LongDivision) ?? -1) >= 0;
    if (isLongDivision) {
        options = settings.HalfPage
            ? HalfPageLongDivisionNumberOfProblemsOptions
            : FullPageLongDivisionNumberOfProblemsOptions;
    }

    // A missing-number sheet is math facts, but written one-per-line, so the
    // 60/100 fact-grid counts overflow it. Only when the setting is actually on.
    if (
        settings.Format === ProblemFormatOptions.MathFacts &&
        settings.MissingNumber &&
        settings.MissingNumber !== MissingNumberOptions.Off &&
        !isLongDivision
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfMissingNumberOptions
            : FullPageNumberOfMissingNumberOptions;
    }

    // A fraction-model question draws one or two bars - taller than a fraction
    // sum, so it does not borrow FullPageNumberOfFractionProblemsOptions
    // (ADR 0043 §Consequences). Checked first, since it is still the Fractions
    // format.
    if (
        settings.Format === ProblemFormatOptions.Fractions &&
        isFractionModelQuestion(settings.FractionQuestion)
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfFractionModelOptions
            : FullPageNumberOfFractionModelOptions;
    } else if (settings.Format === ProblemFormatOptions.Fractions) {
        options = settings.HalfPage
            ? HalfPageNumberOfFractionProblemsOptions
            : FullPageNumberOfFractionProblemsOptions;
    }

    if (settings.Format === ProblemFormatOptions.TellingTime) {
        // Elapsed time shows two times per problem: far fewer per page for two
        // clock faces, more for digital-only. Per-page count now depends on the
        // question type, which no other format needs. ADR 0017 §4.
        if (isElapsedTimeQuestion(settings.TimeQuestion)) {
            const digitalOnly = settings.TimeDisplay === TimeDisplayOptions.TwoDigital;
            const numberLine = settings.TimeDisplay === TimeDisplayOptions.NumberLine;
            options = settings.HalfPage
                ? numberLine
                    ? HalfPageNumberOfElapsedNumberLineOptions
                    : digitalOnly
                    ? HalfPageNumberOfElapsedDigitalOptions
                    : HalfPageNumberOfElapsedTwoClockOptions
                : numberLine
                ? FullPageNumberOfElapsedNumberLineOptions
                : digitalOnly
                ? FullPageNumberOfElapsedDigitalOptions
                : FullPageNumberOfElapsedTwoClockOptions;
        } else if (settings.TimeQuestion === TimeQuestionOptions.AmOrPm) {
            // A sentence and two choices, one to a row (ADR 0090 §6).
            options = settings.HalfPage ? HalfPageNumberOfAmPmOptions : FullPageNumberOfAmPmOptions;
        } else {
            options = settings.HalfPage
                ? HalfPageNumberOfTimeProblemsOptions
                : FullPageNumberOfTimeProblemsOptions;
        }
    }

    // A labelled rectangle is drawn too, and taller than any sentence, but two
    // fit across - so its counts are its own and higher than the line's.
    if (settings.Format === ProblemFormatOptions.Shapes) {
        options = settings.HalfPage
            ? HalfPageNumberOfShapeOptions
            : FullPageNumberOfShapeOptions;
    }

    // A scale drawing adds a line under the rectangle and longer prompts, so it
    // has counts of its own (ADR 0107 As built).
    if (
        settings.Format === ProblemFormatOptions.Shapes &&
        settings.ShapeQuestion === ShapeQuestionOptions.ScaleDrawing
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfScaleDrawingOptions
            : FullPageNumberOfScaleDrawingOptions;
    }

    // A parallelogram, trapezoid or triangle is drawn with a label above and
    // below and its height dashed, so counts of their own (ADR 0121 §6).
    if (
        settings.Format === ProblemFormatOptions.Shapes &&
        isAreaFigureQuestion(settings.ShapeQuestion)
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfAreaFigureOptions
            : FullPageNumberOfAreaFigureOptions;
    }

    // A box is drawn about as tall as a rectangle and sits two to a row, so it
    // starts from the same counts (ADR 0095 §7).
    if (settings.Format === ProblemFormatOptions.Volume) {
        options = settings.HalfPage
            ? HalfPageNumberOfVolumeOptions
            : FullPageNumberOfVolumeOptions;
    }

    // A circle is smaller than a box and sits two to a row (ADR 0106 §4).
    if (settings.Format === ProblemFormatOptions.Circles) {
        options = settings.HalfPage
            ? HalfPageNumberOfCircleOptions
            : FullPageNumberOfCircleOptions;
    }

    // Spinners and a bag of counters are drawn about as big as a circle (ADR 0108 §3).
    if (settings.Format === ProblemFormatOptions.Probability) {
        options = settings.HalfPage
            ? HalfPageNumberOfProbabilityOptions
            : FullPageNumberOfProbabilityOptions;
    }

    // A right triangle is drawn about as big as a circle (ADR 0109 §5).
    if (settings.Format === ProblemFormatOptions.PythagoreanTheorem) {
        options = settings.HalfPage
            ? HalfPageNumberOfPythagoreanOptions
            : FullPageNumberOfPythagoreanOptions;
    }

    // A drawn number line is taller than any sentence - a prompt, a drawing and
    // an answer rule - so it has counts of its own rather than borrowing place
    // value's. Measured: eight spills onto a third page (ADR 0033 §5).
    if (settings.Format === ProblemFormatOptions.NumberLine) {
        options = settings.HalfPage
            ? HalfPageNumberOfNumberLineOptions
            : FullPageNumberOfNumberLineOptions;
    }

    // Number properties ask the same way place value does - a sentence with a
    // rule under it - so they take the same counts (ADR 0032).
    if (
        settings.Format === ProblemFormatOptions.PlaceValue ||
        settings.Format === ProblemFormatOptions.NumberProperties ||
        // A pattern is one full-width row like a place value prompt, and an
        // eight-term sequence is the longest of them.
        settings.Format === ProblemFormatOptions.NumberPatterns ||
        // A percent-of-change question is a sentence with a rule under it, the
        // same shape as a place value prompt; a unit rate prompt is the same.
        settings.Format === ProblemFormatOptions.Percent ||
        settings.Format === ProblemFormatOptions.Ratio ||
        // A list of up to nine values is a sentence too (ADR 0039 §2).
        settings.Format === ProblemFormatOptions.Averages ||
        // A numeral and its answer are shorter than any of them (ADR 0079 §1).
        settings.Format === ProblemFormatOptions.RomanNumerals
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfPlaceValueProblemsOptions
            : FullPageNumberOfPlaceValueProblemsOptions;
    }

    // A money question in a sentence (tax, a discount, interest) is a line longer
    // than any other Percent prompt, so it has counts of its own (ADR 0120 §7).
    if (
        settings.Format === ProblemFormatOptions.Percent &&
        MONEY_PERCENT_QUESTIONS.has(settings.PercentQuestion)
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfPercentMoneyOptions
            : FullPageNumberOfPercentMoneyOptions;
    }

    // A proportional-relationships table is a prompt, a two-row table and an
    // answer rule: taller than the sentence Ratio otherwise is, so it has
    // counts of its own (ADR 0104 §5, 0050).
    if (
        settings.Format === ProblemFormatOptions.Ratio &&
        settings.RatioQuestion === RatioQuestionOptions.Proportional
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfProportionalOptions
            : FullPageNumberOfProportionalOptions;
    }

    // A proportional graph is a square grid big enough to read: counts of its
    // own (ADR 0105 §5).
    if (
        settings.Format === ProblemFormatOptions.Ratio &&
        settings.RatioQuestion === RatioQuestionOptions.ProportionalGraphs
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfProportionalGraphOptions
            : FullPageNumberOfProportionalGraphOptions;
    }

    // Skip counting has only eleven distinct sequences - the multiples of 2
    // through 12 - so the top two counts would print repeats rather than
    // overflow the page. Every other narrowing in this file is about space;
    // this one is about how many questions exist, which is why measuring the
    // page would never have found it. Half-page counts are 3 and 4, well
    // inside eleven, so only the full page narrows. `Mixed` keeps the full
    // range: it draws skip counting for a third of its problems and stays
    // distinct even at sixteen.
    if (
        settings.Format === ProblemFormatOptions.NumberPatterns &&
        settings.PatternQuestion === PatternQuestionOptions.SkipCounting &&
        !settings.HalfPage
    ) {
        options = FullPageNumberOfSkipCountingOptions;
    }

    // A sheet of Roman numerals asks about each value once, so a range of ten
    // cannot fill a page of twelve or sixteen without repeating. Like skip
    // counting above, this is about how many questions exist rather than about
    // space, so measuring the page would never find it (ADR 0079 §4).
    if (settings.Format === ProblemFormatOptions.RomanNumerals) {
        const distinct = distinctRomanValues(settings.RomanMax);
        const fitting = options.filter((count) => count <= distinct);
        options = fitting.length > 0 ? fitting : options.slice(0, 1);
    }

    // A geometry question is a sentence with a rule under it, but a longer
    // sentence than a place value prompt - long enough to wrap - so it gets
    // its own counts rather than borrowing them. Measured: nine fits a full
    // page and ten does not; four fits a half and five does not (ADR 0049).
    if (settings.Format === ProblemFormatOptions.Geometry) {
        options = settings.HalfPage
            ? HalfPageNumberOfGeometryOptions
            : FullPageNumberOfGeometryOptions;
    }

    // A bar chart is the largest thing this site draws - its own counts,
    // lower than the number line's. Measured against the rendered PDF.
    //
    // `Total` and `Largest` are lower again: each has one true answer, so a
    // chart supports at most one, and a sheet pinned to either draws one chart
    // per question instead of one per group. ADR 0040's amortisation - which
    // the ordinary counts were measured under - does not hold for them.
    // ADR 0050 §3.
    if (settings.Format === ProblemFormatOptions.BarGraphs) {
        const oneChartEach =
            settings.BarGraphQuestion === BarGraphQuestionOptions.Total ||
            settings.BarGraphQuestion === BarGraphQuestionOptions.Largest;
        options = settings.HalfPage
            ? oneChartEach
                ? HalfPageNumberOfSingleAnswerBarGraphOptions
                : HalfPageNumberOfBarGraphOptions
            : oneChartEach
            ? FullPageNumberOfSingleAnswerBarGraphOptions
            : FullPageNumberOfBarGraphOptions;
    }

    // A line plot is asked up to four questions, one plot per group, like a
    // bar chart (ADR 0084 §5). A median, mode and range sheet asks three a
    // plot, and a sheet pinned to a one-answer question one, so each has
    // counts of its own: the number of plots, not of questions, is what fills
    // the page. Measured against the rendered PDF.
    if (settings.Format === ProblemFormatOptions.LinePlots) {
        const question = settings.LinePlotQuestion;
        if (SINGLE_ANSWER_LINE_PLOT_QUESTIONS.includes(question)) {
            options = settings.HalfPage
                ? HalfPageNumberOfSingleAnswerLinePlotOptions
                : FullPageNumberOfSingleAnswerLinePlotOptions;
        } else if (question === LinePlotQuestionOptions.CenterAndSpread) {
            options = settings.HalfPage
                ? HalfPageNumberOfCenterAndSpreadLinePlotOptions
                : FullPageNumberOfCenterAndSpreadLinePlotOptions;
        } else {
            options = settings.HalfPage ? HalfPageNumberOfLinePlotOptions : FullPageNumberOfLinePlotOptions;
        }
    }

    // Three questions a drawing, so a count is whole drawings (ADR 0099 §4).
    // Box plots are shorter than histograms. Measured against the rendered PDF.
    //
    // A sheet pinned to a one-answer question draws a drawing a question, so
    // it counts drawings (ADR 0050 §3), as a pinned bar-graph Total sheet does.
    if (settings.Format === ProblemFormatOptions.Histograms) {
        options = SINGLE_ANSWER_HISTOGRAM_QUESTIONS.includes(settings.HistogramQuestion)
            ? (settings.HalfPage ? HalfPageNumberOfSingleAnswerHistogramOptions : FullPageNumberOfSingleAnswerHistogramOptions)
            : (settings.HalfPage ? HalfPageNumberOfHistogramOptions : FullPageNumberOfHistogramOptions);
    }
    if (settings.Format === ProblemFormatOptions.BoxPlots) {
        options = SINGLE_ANSWER_BOX_PLOT_QUESTIONS.includes(settings.BoxPlotQuestion)
            ? (settings.HalfPage ? HalfPageNumberOfSingleAnswerBoxPlotOptions : FullPageNumberOfSingleAnswerBoxPlotOptions)
            : settings.BoxPlotQuestion === BoxPlotQuestionOptions.Quartile
                ? (settings.HalfPage ? HalfPageNumberOfQuartileBoxPlotOptions : FullPageNumberOfQuartileBoxPlotOptions)
                : (settings.HalfPage ? HalfPageNumberOfBoxPlotOptions : FullPageNumberOfBoxPlotOptions);
    }

    // Four questions beside each grid (ADR 0085 §1). Every question type can
    // fill four on one grid, so one set of counts serves them all. Measured
    // against the rendered PDF, and looked at.
    if (settings.Format === ProblemFormatOptions.CoordinatePlane) {
        options = settings.CoordinateQuestion === CoordinateQuestionOptions.Triangles
            ? (settings.HalfPage ? HalfPageNumberOfTriangleOptions : FullPageNumberOfTriangleOptions)
            : settings.CoordinateQuestion === CoordinateQuestionOptions.MissingCorner
                ? (settings.HalfPage ? HalfPageNumberOfMissingCornerOptions : FullPageNumberOfMissingCornerOptions)
                : settings.CoordinateQuestion === CoordinateQuestionOptions.SlopeFromGraph
                    ? (settings.HalfPage ? HalfPageNumberOfSlopeGraphOptions : FullPageNumberOfSlopeGraphOptions)
                    : settings.CoordinateQuestion === CoordinateQuestionOptions.SlopeFromPoints
                        ? (settings.HalfPage ? HalfPageNumberOfSlopePointsOptions : FullPageNumberOfSlopePointsOptions)
                        : (settings.HalfPage ? HalfPageNumberOfCoordinateOptions : FullPageNumberOfCoordinateOptions);
    }

    // One month a page, one question to a row under it (ADR 0093 §2, §8).
    // Measured against the rendered PDF, and looked at.
    if (settings.Format === ProblemFormatOptions.Calendar) {
        options = settings.HalfPage ? HalfPageNumberOfCalendarOptions : FullPageNumberOfCalendarOptions;
        // A month has eight How Many questions, one per weekday and its
        // length, so a sheet pinned to them stops at eight.
        if (settings.CalendarQuestion === CalendarQuestionOptions.HowMany) {
            options = options.filter((count) => count <= CALENDAR_HOW_MANY_QUESTIONS);
        }
    }

    // Magic squares, two across. Like Roman numerals, a small size has few different
    // squares, so a count is offered only if that many can all differ (ADR 0088 §5).
    if (settings.Format === ProblemFormatOptions.MagicSquares) {
        const all = settings.HalfPage ? HalfPageNumberOfMagicSquareOptions : FullPageNumberOfMagicSquareOptions;
        const distinct = distinctMagicSquares(settings.MagicSquareMax);
        const fitting = all.filter((count) => count <= distinct);
        options = fitting.length > 0 ? fitting : all.slice(0, 1);
    }

    if (settings.Format === ProblemFormatOptions.FactFamily) {
        options = settings.HalfPage
            ? HalfPageNumberOfFactFamilyOptions
            : FullPageNumberOfFactFamilyOptions;
    }

    // Algebra and integers are the same width class - a full-width expression
    // row - so they inherit the order-of-operations counts rather than each
    // declaring its own.
    if (
        settings.Format === ProblemFormatOptions.OrderOfOperations ||
        settings.Format === ProblemFormatOptions.Algebra ||
        settings.Format === ProblemFormatOptions.Integers ||
        settings.Format === ProblemFormatOptions.UnitConversion
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfOrderOfOperationsOptions
            : FullPageNumberOfOrderOfOperationsOptions;
    }

    // Decimals with signs are a longer line than an integer's, and a signed
    // fraction is a stack tall, so the rational types have counts of their own
    // (ADR 0122 §6).
    if (
        settings.Format === ProblemFormatOptions.Integers &&
        isRationalQuestion(settings.IntegerQuestion)
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfRationalOptions
            : FullPageNumberOfRationalOptions;
    }

    // A worded phrase is a row to itself and may wrap, so Writing Expressions has
    // counts of its own, not the other Algebra types' (ADR 0123 §6).
    if (
        settings.Format === ProblemFormatOptions.Algebra &&
        settings.AlgebraQuestion === AlgebraQuestionOptions.WritingExpressions
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfWritingExpressionOptions
            : FullPageNumberOfWritingExpressionOptions;
    }

    // A system is three lines to a problem: the two equations and the answer
    // line (ADR 0139 §5).
    if (
        settings.Format === ProblemFormatOptions.Algebra &&
        settings.AlgebraQuestion === AlgebraQuestionOptions.SystemsOfEquations
    ) {
        options = settings.HalfPage
            ? HalfPageNumberOfSystemsOptions
            : FullPageNumberOfSystemsOptions;
    }

    if (settings.Format === ProblemFormatOptions.RoundingComparing) {
        // A number line (ADR 0046) is a full-width drawing, the same width
        // class as elapsed time's own line (ADR 0044) - far fewer per page
        // than the plain sentence or the packed comparison.
        const numberLine = settings.RoundingDisplay === RoundingDisplayOptions.NumberLine;
        options = settings.HalfPage
            ? numberLine
                ? HalfPageNumberOfRoundingNumberLineOptions
                : HalfPageNumberOfRoundingComparingOptions
            : numberLine
            ? FullPageNumberOfRoundingNumberLineOptions
            : FullPageNumberOfRoundingComparingOptions;
    }

    if (
        settings.Format === ProblemFormatOptions.Money ||
        settings.Format === ProblemFormatOptions.Decimals
    ) {
        // A counting-coins problem is a wide row of drawn discs; making change is
        // a short text block. Both are a different width class from the vertical
        // money column, so they get their own arrays. ADR 0018 §6.
        if (
            settings.Format === ProblemFormatOptions.Money &&
            settings.MoneyQuestion === MoneyQuestionOptions.WordProblems
        ) {
            // Told as stories, a money problem is laid out exactly like a
            // Word Problems one, so it takes the same measured counts. ADR 0063.
            options = settings.HalfPage
                ? HalfPageNumberOfWordProblemsOptions
                : FullPageNumberOfWordProblemsOptions;
        } else if (
            settings.Format === ProblemFormatOptions.Money &&
            (settings.MoneyQuestion === MoneyQuestionOptions.CountingCoins ||
                // A mixed half page holds coin rows, and a coin row is its
                // tallest problem, so it takes the coin counts. On the change
                // counts, the larger coins of PR #224 wrap a row and push the
                // last problem onto the footer at 6 and 8 (ADR 0089 As built).
                // A full page has the room and keeps the change counts below.
                (settings.MoneyQuestion === MoneyQuestionOptions.Mixed && settings.HalfPage))
        ) {
            options = settings.HalfPage
                ? HalfPageNumberOfCoinProblemsOptions
                : FullPageNumberOfCoinProblemsOptions;
        } else if (
            settings.Format === ProblemFormatOptions.Money &&
            (settings.MoneyQuestion === MoneyQuestionOptions.MakingChange ||
                settings.MoneyQuestion === MoneyQuestionOptions.Mixed)
        ) {
            options = settings.HalfPage
                ? HalfPageNumberOfChangeProblemsOptions
                : FullPageNumberOfChangeProblemsOptions;
        } else {
            // Decimals sit between facts and fractions in width; the money counts
            // are already measured for numbers of that size.
            options = settings.HalfPage
                ? HalfPageNumberOfMoneyProblemsOptions
                : FullPageNumberOfMoneyProblemsOptions;
        }
    }

    if (settings.Format === ProblemFormatOptions.WordProblem) {
        options = settings.HalfPage
            ? HalfPageNumberOfWordProblemsOptions
            : FullPageNumberOfWordProblemsOptions;
    }

    // Large print is a layout, and a layout owns a per-page count (ADR 0050
    // §1). Applied last, because it overrides whichever format branch ran:
    // `showsLargePrint` offers the control on math facts, money and decimals,
    // and Page.tsx drops only math facts to four across, so the two take
    // different counts.
    if (settings.Presentation?.largePrint) {
        if (settings.Format === ProblemFormatOptions.MathFacts) {
            options = settings.HalfPage ? HalfPageLargePrintOptions : FullPageLargePrintOptions;
        } else if (
            settings.Format === ProblemFormatOptions.Money ||
            settings.Format === ProblemFormatOptions.Decimals
        ) {
            options = settings.HalfPage
                ? HalfPageLargePrintMoneyOptions
                : FullPageLargePrintMoneyOptions;
        }
    }

    // One picture, one count: every cell is a problem (ADR 0058 §5). Last, so
    // nothing above - a stray large-print flag included - can override it.
    if (settings.Format === ProblemFormatOptions.ColorByNumber) {
        options = [COLOR_BY_NUMBER_CELLS];
    }

    // One riddle, one count: a problem for each letter of its punchline, so the
    // longest in the bank is the most a page holds (ADR 0088 §5). Last, as color
    // by number's is.
    if (settings.Format === ProblemFormatOptions.RiddleDecoder) {
        options = [RIDDLE_MAX_LETTERS];
    }

    return options;
}

/**
 * Pulls `NumberOfProblemsPerPage` down to the largest count the sheet offers.
 *
 * Mutates, because that is what the panel did inline before this was lifted
 * out and the generator reads the value straight off the shared settings
 * object. The panel no longer calls it (ADR 0082): the generator derives its
 * settings through `withPerPageClamped`, below. This stays for the two scripts
 * that clamp a settings object they own, `render.thumbs.js` and the page
 * measurements.
 *
 * One behaviour *does* change, deliberately: the clamp now runs for every
 * format. It used to be repeated inside each branch and was simply absent from
 * two of them - plain math facts and long division - so switching a 100-problem
 * sheet to a half page left it at 100 with 40 the largest count offered. The
 * radio group showed nothing selected and the sheet generated 100 anyway.
 */
export function clampPerPageToOptions(settings: ISettingsProps): number[] {
    const options = perPageOptionsFor(settings);
    const ceiling = options[options.length - 1];
    if (settings.NumberOfProblemsPerPage > ceiling) {
        settings.NumberOfProblemsPerPage = ceiling;
    }
    return options;
}

/**
 * The settings the sheet is built from: `settings` with the per-page count
 * pulled down to the largest count the sheet offers (ADR 0082). A pure
 * function of its argument, returning the same object when nothing needs
 * pulling down so a memo keyed on it holds.
 *
 * It does what the panel's inline clamp did, including the one case it never
 * reached: a spiral-review sheet has no per-page control (its section counts
 * are the count, ADR 0013), the panel never rendered one, so its count was
 * never touched, and it is not here.
 */
export function withPerPageClamped(settings: ISettingsProps): ISettingsProps {
    if (settings.Format === ProblemFormatOptions.SpiralReview) {
        return settings;
    }
    const options = perPageOptionsFor(settings);
    const ceiling = options[options.length - 1];
    return settings.NumberOfProblemsPerPage > ceiling
        ? { ...settings, NumberOfProblemsPerPage: ceiling }
        : settings;
}
